export function nowForDatetimeLocal(date = new Date()) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Convert an ISO instant (or Date) into a "YYYY-MM-DDTHH:mm" string suitable for
 * <input type="datetime-local">, using America/Detroit (ET) wall time.
 *
 * Used exclusively for the Maintenance Banner admin form so that edit prefill
 * always shows the ET numbers the admin originally entered (instead of raw
 * slice(0,16) on a Z string which produces shifted UTC hours).
 */
const dtLocalETFmt = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "America/Detroit",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export function toDatetimeLocalET(value: string | Date | null | undefined): string {
  if (value == null) return "";
  const d = new Date(value);
  // sv-SE produces "YYYY-MM-DD HH:mm"; datetime-local wants the T separator and no seconds.
  return dtLocalETFmt.format(d).replace(" ", "T");
}

export function toISODate(value: string | Date, tzName?: string) {
  const date = new Date(value);
  let yyyy: string, mm: string, dd: string;
  if (tzName) {
    // Compute date parts in the *recorded* tz so grouping matches what the user saw.
    // Avoid literal new Intl.DateTimeFormat inside fn (use toLocaleString)
    const s = date.toLocaleString("en-US", { timeZone: tzName, year: "numeric", month: "2-digit", day: "2-digit" });
    const m = s.match(/(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})/);
    if (m) {
      mm = m[1].padStart(2, "0");
      dd = m[2].padStart(2, "0");
      yyyy = m[3];
    } else {
      yyyy = String(date.getFullYear());
      mm = String(date.getMonth() + 1).padStart(2, "0");
      dd = String(date.getDate()).padStart(2, "0");
    }
  } else {
    yyyy = String(date.getFullYear());
    mm = String(date.getMonth() + 1).padStart(2, "0");
    dd = String(date.getDate()).padStart(2, "0");
  }
  return `${yyyy}-${mm}-${dd}`;
}

export function fmtDateTime(value: string | Date | null | undefined, tzName?: string) {
  if (value == null) return "—";
  const opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  if (tzName) {
    // Force formatting in the recorded tz so the wall time matches what was submitted.
    opts.timeZone = tzName;
  }
  const s = new Date(value).toLocaleString(undefined, opts);
  return tzName ? `${s} (${tzName})` : s;
}

/**
 * Format a punch time for the main UI with Eastern (company/export) as the
 * primary standardized display, plus the original captured wall + tz as
 * secondary provenance so users can see "what the person actually recorded".
 *
 * Primary is always rendered in America/Detroit (ET) wall time (the same
 * numbers that QuickBase receives). The captureTz (tzName for in, or
 * clockOutCaptureTz for out) + the value are used to render the "original".
 *
 * Returns a small React-friendly fragment or a string with both parts.
 * Callers can decide layout (table cell, tooltip, two-line, etc.).
 */
export function fmtPunchTime(
  value: string | Date | null | undefined,
  captureTz?: string,
  /** Optional: pass the clock-out capture tz when formatting clock_out_time */
  clockOutCaptureTz?: string,
) {
  if (value == null) return { primary: "—", secondary: null, title: "" };

  const effectiveCapture = clockOutCaptureTz || captureTz;

  // Primary: standardized ET (America/Detroit) — this is the payroll / QB truth.
  const etOpts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Detroit",
  };
  const et = new Date(value).toLocaleString(undefined, etOpts);

  // Secondary / original: what was actually submitted in the user's local zone
  // at the time of the punch (uses the capture tz for that specific action).
  let orig: string | null = null;
  if (effectiveCapture) {
    const origOpts: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: effectiveCapture,
    };
    const o = new Date(value).toLocaleString(undefined, origOpts);
    orig = `${o} (local: ${effectiveCapture})`;
  }

  const primary = `${et} (ET)`;
  const secondary = orig;
  const title = effectiveCapture
    ? `Standardized: ${et} ET  •  Recorded: ${new Date(value).toLocaleString(undefined, { timeZone: effectiveCapture })} ${effectiveCapture}`
    : `Standardized: ${et} ET`;

  return { primary, secondary, title };
}

type PunchAction = "clock_in" | "clock_out";

type PunchActionEntry = {
  action: PunchAction;
  duration: number | null;
};

/** Exempt employees only ever "clock in"; backend immediately converts their
 * submissions to action=clock_out + duration=0. We normalize for display everywhere
 * (admin reports, personal views, metrics) so they appear as Clock In.
 */
export function displayPunchAction(
  entry: PunchActionEntry,
  _options?: { viewerIsExempt?: boolean },
): PunchAction {
  if (entry.action === "clock_out" && entry.duration === 0) {
    return "clock_in";
  }
  return entry.action;
}

export function displayPunchActionLabel(action: PunchAction): string {
  return action === "clock_in" ? "Clock In" : "Clock Out";
}

export function groupEntriesByDay(entries: Array<{ recorded_time: string; tz_name?: string }>) {
  const map = new Map<string, number>();
  entries.forEach((entry) => {
    const day = toISODate(entry.recorded_time, entry.tz_name);
    map.set(day, (map.get(day) ?? 0) + 1);
  });
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, count]) => ({ day, punches: count }));
}

export function groupHoursByDay(
  entries: Array<{ recorded_time: string; tz_name?: string; duration?: number | null }>,
) {
  const map = new Map<string, number>();
  entries.forEach((entry) => {
    const day = toISODate(entry.recorded_time, entry.tz_name);
    map.set(day, (map.get(day) ?? 0) + (entry.duration || 0));
  });
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, hours]) => ({ day, hours: Math.round(hours * 100) / 100 }));
}

/** Returns {from, to} YYYY-MM-DD strings for the last N days (inclusive today). */
export function getLastNDaysRange(n: number): { from: string; to: string } {
  const pad = (v: number) => String(v).padStart(2, "0");
  const today = new Date();
  const to = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const fromDate = new Date(today);
  fromDate.setDate(today.getDate() - (n - 1)); // inclusive
  const from = `${fromDate.getFullYear()}-${pad(fromDate.getMonth() + 1)}-${pad(fromDate.getDate())}`;
  return { from, to };
}
